/**
 * "What the cycle found" for the exit decision run — the rules over its digest
 * (`GET /exit/consolidation/runs/{run_id}/digest`, bata-platform ADR 7023; `docs/adr/9019`).
 *
 * Moved from the Cockpit to the exit chapter in package A (2026-09-30, DEC-87: the Cockpit is
 * synthesis only), and from there to Management ▸ Cycles ▸ Findings with its «Module» selector
 * (issue 226, giro 3 «Dopo» point 3, owner ruling 2026-10-07). It sits beside the in-season half
 * (`cycle-findings.ts`) now, so `uniqueLabels` is that module's, no longer stated twice.
 *
 * The words for the reason codes and door roles the digest groups by are this page's own
 * (`EXIT_REASON_LABEL_KEY`, `EXIT_ROLE_LABEL_KEY`): the exit chapter's maps cannot be imported
 * from here, and the digest groups by the four clearance reasons and three node roles only. A
 * code outside them is drawn as the code, never under a guessed name.
 *
 * Pure, so `cycle-findings.test.ts` pins every rule on strings and numbers.
 */

import type { ConsolidationRunDigestSummary } from '@/api/generated';
import { formatCompactNumber } from '@/lib/format';
import { defineHeadline } from '@/planning';
import type { ChartSpec } from '@/charts';

import type { Label } from './cycle-findings';

/** The clearance reasons a consolidation lane is grouped by (`planning.exit.clearance`), and the units-sent cap's. */
export const EXIT_REASON_LABEL_KEY: Readonly<Record<string, string>> = {
  size_run_repair: 'inseason.cycles.findings.exit.reason.sizeRunRepair',
  door_consolidated: 'inseason.cycles.findings.exit.reason.doorConsolidated',
  transfer_uneconomic: 'inseason.cycles.findings.exit.reason.laneNotWorthIt',
  lane_not_costed: 'inseason.cycles.findings.exit.reason.awaitingLaneCost',
  // bata-reqs ADR 9000 (platform #888): a line the sending store's weekly pairs-sent cap held back.
  outbound_units_cap_deferred: 'inseason.cycles.findings.exit.reason.outboundUnitsCapDeferred',
};

/** The node roles the clearance engine assigns a door; `neither` is a position-only record. */
export const EXIT_ROLE_LABEL_KEY: Readonly<Record<string, string>> = {
  source: 'inseason.cycles.findings.exit.role.source',
  destination: 'inseason.cycles.findings.exit.role.destination',
  neither: 'inseason.cycles.findings.exit.role.neither',
};

/** The key of a code in one of the maps above, or `null` for a code this build does not know. */
export function exitLabelKey(map: Readonly<Record<string, string>>, code: string): string | null {
  return Object.prototype.hasOwnProperty.call(map, code) ? map[code] ?? null : null;
}

export const exitFoundHeadline = defineHeadline({
  keys: {
    nothing: 'inseason.found.exit.headline.nothing',
    withheld: 'inseason.found.exit.headline.withheld',
    proposed: 'inseason.found.exit.headline.proposed',
  },
  choose: (digest: ConsolidationRunDigestSummary, { locale }) => {
    const book = digest.lanes;
    if (book.lanes === 0) return { variant: 'nothing' };
    if (book.proposed_lanes === 0) return { variant: 'withheld', values: { lanes: book.lanes } };
    return {
      variant: 'proposed',
      values: {
        proposed: book.proposed_lanes,
        lanes: book.lanes,
        units: formatCompactNumber(book.proposed_units, locale).text,
      },
    };
  },
});

/** Lane units by the reason that decided them — proposed units, one bar per reason. */
export function laneReasonSpec(digest: ConsolidationRunDigestSummary, label: Label): ChartSpec | null {
  const reasons = digest.lanes.by_reason;
  if (reasons.length === 0) return null;
  return {
    form: 'bar',
    orientation: 'horizontal',
    actionTitleKey: 'inseason.found.exit.reasonTitle',
    actionTitleValues: { reasons: reasons.length },
    perimeterKey: 'inseason.found.exit.reasonPerimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.exit.reasonAxis', order: reasons.map((row) => label(row.reason_code)) },
    series: [{
      id: 'units', nameKey: 'inseason.found.exit.unitsSeries',
      data: reasons.map((row) => row.units), distinguisher: 'pattern-solid', slot: 1,
    }],
  };
}

/** Residual units by door role, at the position run's own as-of. `null` with no residual. */
export function residualRoleSpec(digest: ConsolidationRunDigestSummary, label: Label): ChartSpec | null {
  const residual = digest.residual;
  if (residual === null || residual === undefined || residual.by_node_role.length === 0) return null;
  return {
    form: 'bar',
    orientation: 'horizontal',
    actionTitleKey: 'inseason.found.exit.residualTitle',
    actionTitleValues: { doors: residual.doors },
    perimeterKey: 'inseason.found.exit.residualPerimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.exit.roleAxis', order: residual.by_node_role.map((row) => label(row.key)) },
    series: [{
      id: 'residual', nameKey: 'inseason.found.exit.residualSeries',
      data: residual.by_node_role.map((row) => row.residual_units), distinguisher: 'pattern-solid', slot: 2,
    }],
  };
}
